import { Box } from '@mui/material';
import { ReactNode, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useLocation } from 'react-router-dom';

import { CatalogItemsSelectedCounter } from 'entities/CatalogItem';
import {
    getCatalogItems,
    getCatalogItemsSelectedIsEmpty,
    getCatalogItemsShowDetailedView,
    getSelectedCatalogItem,
} from 'entities/CatalogItem/model/selectors/catalogItemSelectors';
import { getSelectCategoryId } from 'entities/Category/model/selectors/categorySelectors';
import { getAddCatalogItemMode } from 'features/AddCatalogItem/model/selectors/addCatalogItemSelectors';
import { MoveToInventoryDialog } from 'features/moveToInventoryCatalogItem/ui/MoveToInventoryDialog/MoveToInventoryDialog';
import { RemoveCatalogItemDialog } from 'features/removeCatalogItem/ui/RemoveCatalogItemDialog/RemoveCatalogItemDialog';
import { PropertyCatalogItemsMoveTo } from 'widgets/PropertyCatalog';

import { PropertyCatalogEmpty } from './PropertyCatalogEmpty';

import type { CatalogCategoryChangeFollowUp } from 'features/changeCatalogItemCategory';

type PropertyCatalogItemsProps = {
    showCatalogItems: boolean;
    hideActionPanel?: boolean;
    moveTo?: PropertyCatalogItemsMoveTo;
    actionButtons?: ReactNode;
    onAfterCatalogCategoryChange?: CatalogCategoryChangeFollowUp;
};

export const PropertyCatalogItems = ({
    showCatalogItems,
    hideActionPanel,
    moveTo = PropertyCatalogItemsMoveTo.CATALOG,
    actionButtons,
    onAfterCatalogCategoryChange,
}: PropertyCatalogItemsProps) => {
    const location = useLocation();

    const selectedCategory = useSelector(getSelectCategoryId);
    const catalogItems = useSelector(getCatalogItems);
    const hasCatalogItems = catalogItems && catalogItems?.length;
    const catalogItemsListSelectionIsEmpty = useSelector(
        getCatalogItemsSelectedIsEmpty,
    );
    const isEditingCatalog = useSelector(getAddCatalogItemMode);

    const detailedViewIsShown = useSelector(getCatalogItemsShowDetailedView);
    const selectedItem = useSelector(getSelectedCatalogItem);

    const moveToCatalogMode = moveTo === PropertyCatalogItemsMoveTo.CATALOG;
    const moveToInventory = moveTo === PropertyCatalogItemsMoveTo.INVENTORY;
    const isOrderEditing = location.state?.selectedOrderId;

    // Orders/lists add-items show selection count in ReviewCatalogSelectionFooter instead.
    const isAddItemsSelectionFlow =
        moveTo === PropertyCatalogItemsMoveTo.ORDER ||
        moveTo === PropertyCatalogItemsMoveTo.CUSTOM_LIST;
    const shouldRenderActionPanel = !(
        hideActionPanel ||
        (isAddItemsSelectionFlow && !actionButtons)
    );
    const showSelectedCounter =
        !isAddItemsSelectionFlow && !catalogItemsListSelectionIsEmpty;
    const showActionPanel =
        shouldRenderActionPanel &&
        (showSelectedCounter || Boolean(actionButtons));
    const selectedCounterAlign = actionButtons ? 'start' : 'end';
    const selectedCounterJustifyContent = actionButtons
        ? 'flex-start'
        : 'flex-end';

    const showBulkPanel =
        moveToCatalogMode &&
        !catalogItemsListSelectionIsEmpty &&
        !isEditingCatalog;

    const selectedCategoryIsEmpty = Boolean(
        !hasCatalogItems && selectedCategory,
    );

    const itemDetailedOutletContext = useMemo(
        () => ({
            isDetailed: true,
            item: selectedItem,
            moveTo: moveTo,
            quickEdit: isOrderEditing,
        }),
        [isOrderEditing, moveTo, selectedItem],
    );

    const catalogItemsOutletContext = useMemo(
        () => ({
            hasCatalogItems,
            showBulkPanel,
            showActionPanel,
            moveTo,
            onAfterCatalogCategoryChange,
        }),
        [
            hasCatalogItems,
            moveTo,
            onAfterCatalogCategoryChange,
            showActionPanel,
            showBulkPanel,
        ],
    );

    if (detailedViewIsShown && selectedItem) {
        return (
            <Box p="16px 0 16px 16px" display="flex" height="100%">
                <Outlet context={itemDetailedOutletContext} />
            </Box>
        );
    }

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: '0',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {showCatalogItems ? (
                    <Outlet context={catalogItemsOutletContext} />
                ) : (
                    <PropertyCatalogEmpty
                        selectedCategoryIsEmpty={selectedCategoryIsEmpty}
                    />
                )}

                {showActionPanel ? (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            zIndex: 2,
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            width: '100%',
                            borderTopWidth: '1px',
                            borderTopStyle: 'solid',
                            borderTopColor: 'divider',
                            padding: '16px',
                            backgroundColor: 'background.paper',
                            borderRadius: '0 0 4px 0',
                            minHeight: '58px',
                            boxSizing: 'border-box',
                        }}
                    >
                        {showSelectedCounter ? (
                            <Box
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent:
                                        selectedCounterJustifyContent,
                                }}
                            >
                                <CatalogItemsSelectedCounter
                                    align={selectedCounterAlign}
                                />
                            </Box>
                        ) : null}
                        {actionButtons ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '16px',
                                    flexShrink: 0,
                                    marginLeft: 'auto',
                                }}
                            >
                                {actionButtons}
                            </Box>
                        ) : null}
                    </Box>
                ) : null}
            </Box>
            {!moveToInventory && (
                <>
                    <MoveToInventoryDialog />
                    <RemoveCatalogItemDialog />
                </>
            )}
        </>
    );
};
